import { api, TaskItem, TaskStatus, Sprint, ProjectDetail } from '../api';
import { statusMeta, STATUSES } from '../lib/status';
import { escapeHtml, formatDate } from '../lib/utils';
import { openTaskModal } from '../components/taskModal';
import { openTaskForm } from '../components/taskForm';
import { openModal } from '../components/modal';
import { toast } from '../components/toast';
import { ProjectContext } from './project';

export async function renderBoard(
  content: HTMLElement,
  ctx: ProjectContext,
  sprint?: Sprint | null
): Promise<void> {
  const { project, tasks, epics, reload, readOnly } = ctx;

  let boardTasks = tasks;
  if (sprint) {
    boardTasks = tasks.filter((t) => t.sprintId === sprint.id);
    // A past sprint shows only what was completed during that sprint.
    if (!sprint.active) {
      boardTasks = boardTasks.filter((t) => t.status === 'complete');
    }
  }

  // Owners can edit the active sprint board; past sprints and share views
  // are read-only.
  const editable = !readOnly && (!sprint || sprint.active);

  const byStatus = (status: TaskStatus) =>
    boardTasks.filter((t) => t.status === status);
  const epicLabel = (id: string | null) =>
    id ? epics.find((e) => e.id === id)?.name || '' : '';

  content.innerHTML = `
    ${
      sprint
        ? `
      <div style="margin-bottom:20px;">
        <a class="nav-link" style="padding:0;display:inline-block;margin-bottom:12px;" href="#${ctx.projectHref('sprints')}">← Back to Sprints</a>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;">
          <div>
            <h1 style="font-size:24px;margin:0 0 4px;">${escapeHtml(sprint.name)}</h1>
            <p class="muted" style="margin:0;">
              Start date: ${formatDate(sprint.startDate)}
              ${sprint.endDate ? ` · Ended: ${formatDate(sprint.endDate)}` : ''}
            </p>
          </div>
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
            <span class="badge" style="color:${sprint.active ? '#16a34a' : '#6b7280'};background:${sprint.active ? '#f0fdf4' : '#f3f4f6'};">
              ${sprint.active ? 'Active' : 'Past'}
            </span>
            ${editable ? '<button class="btn btn-ghost btn-sm" id="pull-open-tasks">Pull open tasks</button>' : ''}
          </div>
        </div>
        ${
          !sprint.active
            ? `<p class="muted" style="margin:12px 0 0;">This sprint has ended — showing the tasks completed during it.</p>`
            : ''
        }
      </div>
    `
        : ''
    }
    <p class="muted" style="margin-bottom:16px;">
      ${
        editable
          ? 'Drag tasks between columns to change their status, or click a task to see details.'
          : readOnly
            ? 'Read-only view — click a task to see its details.'
            : 'This is a past sprint — click a task to see its details.'
      }
    </p>
    <div class="board">
      ${STATUSES.map((s) => {
        const items = byStatus(s.key);
        return `
          <div class="board-col" data-col="${s.key}">
            <div class="board-col-head">
              <span class="badge" style="color:${s.color};background:${s.bg}">
                <span class="dot"></span>${s.label}
              </span>
              <span class="board-col-count">${items.length}</span>
            </div>
            <div class="board-col-body">
              ${items
                .map((t) => {
                  const subtaskCount = tasks.filter((x) => x.parentId === t.id).length;
                  const isChild = !!t.parentId;
                  const epic = epicLabel(t.epicId);
                  return `
                <div class="board-card" draggable="${editable ? 'true' : 'false'}" data-id="${t.id}">
                  <div class="board-card-title">
                    ${isChild ? '<span class="muted">↳ </span>' : ''}${escapeHtml(t.title)}
                  </div>
                  ${
                    epic
                      ? `<div style="margin-bottom:8px;"><span class="badge" style="color:#7c3aed;background:#f5f3ff;font-size:10px;padding:2px 8px;">◆ ${escapeHtml(epic)}</span></div>`
                      : ''
                  }
                  <div class="board-card-meta">
                    <span class="board-card-assignee">${escapeHtml(t.assigneeName || 'Unassigned')}</span>
                    <span style="display:flex;gap:6px;align-items:center;">
                      ${subtaskCount ? `<span class="muted" style="font-size:11px;">${subtaskCount} ↳</span>` : ''}
                      <span class="points-chip">${t.storyPoints}</span>
                    </span>
                  </div>
                </div>
              `;
                })
                .join('')}
              ${editable ? `<button class="board-add" data-add="${s.key}">+ Add task</button>` : ''}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;

  // Click card → open detail
  content.querySelectorAll<HTMLElement>('.board-card').forEach((card) => {
    card.addEventListener('click', () => {
      const task = tasks.find((t) => t.id === card.dataset.id!);
      if (task) {
        openTaskModal(
          task,
          project,
          tasks,
          epics,
          reload,
          readOnly || !editable,
          ctx.canComment
        );
      }
    });
  });

  if (!editable) return;

  content.querySelector('#pull-open-tasks')?.addEventListener('click', () => {
    if (sprint) openPullTasksModal(project, sprint, tasks, reload);
  });

  // Add task buttons
  content.querySelectorAll<HTMLElement>('[data-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      openTaskForm(project, {
        initialStatus: btn.dataset.add as TaskStatus,
        epics,
        tasks,
        sprintId: sprint ? sprint.id : null,
        onChanged: reload,
      });
    });
  });

  // Drag & drop
  content.querySelectorAll<HTMLElement>('.board-card').forEach((card) => {
    card.addEventListener('dragstart', (e) => {
      (e as DragEvent).dataTransfer?.setData('text/plain', card.dataset.id!);
      card.classList.add('dragging');
    });
    card.addEventListener('dragend', () => card.classList.remove('dragging'));
  });

  content.querySelectorAll<HTMLElement>('.board-col').forEach((col) => {
    col.addEventListener('dragover', (e) => {
      e.preventDefault();
      col.classList.add('drag-over');
    });
    col.addEventListener('dragleave', () => col.classList.remove('drag-over'));
    col.addEventListener('drop', async (e) => {
      e.preventDefault();
      col.classList.remove('drag-over');
      const id = (e as DragEvent).dataTransfer?.getData('text/plain');
      const status = col.dataset.col as TaskStatus;
      if (!id || !status) return;
      try {
        await api.patch(`/projects/${project.id}/tasks/${id}`, { status });
        toast('Moved to ' + (STATUSES.find((s) => s.key === status)?.label || status));
        reload();
      } catch (err: any) {
        toast(err.message || 'Could not move task', 'error');
      }
    });
  });
}

function openPullTasksModal(
  project: ProjectDetail,
  sprint: Sprint,
  tasks: TaskItem[],
  onChanged: () => void
): void {
  const candidates = tasks
    .filter((t) => t.status !== 'complete' && t.sprintId !== sprint.id)
    .sort((a, b) => a.title.localeCompare(b.title));

  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:6px;">Pull open tasks</h2>
    <p class="muted" style="margin:0 0 16px;">
      Select unfinished tasks to move into <strong>${escapeHtml(sprint.name)}</strong>.
    </p>
    ${
      candidates.length === 0
        ? `<div class="empty-state" style="padding:24px;"><h3>No open tasks to pull</h3><p class="muted">All unfinished tasks are already in this sprint.</p></div>`
        : `
    <form id="pull-tasks-form">
      <div class="pull-task-list">
        ${candidates
          .map((t) => {
            const meta = statusMeta(t.status);
            const origin = t.sprintId ? 'In another sprint' : 'No sprint';
            return `
            <label class="pull-task-row">
              <input type="checkbox" name="task" value="${t.id}" />
              <span class="pull-task-title">${escapeHtml(t.title)}</span>
              <span class="badge" style="color:${meta.color};background:${meta.bg};"><span class="dot"></span>${meta.label}</span>
              <span class="points-chip">${t.storyPoints}</span>
              <span class="pull-task-origin">${origin}</span>
            </label>
          `;
          })
          .join('')}
      </div>
      <div class="form-error hidden" id="pull-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:16px;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Add selected</button>
      </div>
    </form>
    `
    }
  `,
    (body, close) => {
      body.querySelector('[data-close]')?.addEventListener('click', close);
      const form = body.querySelector('#pull-tasks-form') as HTMLFormElement | null;
      const errorEl = body.querySelector('#pull-error') as HTMLElement | null;
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl?.classList.add('hidden');
        const selected = Array.from(
          body.querySelectorAll<HTMLInputElement>('input[name="task"]:checked')
        ).map((i) => i.value);
        if (selected.length === 0) {
          if (errorEl) {
            errorEl.textContent = 'Select at least one task';
            errorEl.classList.remove('hidden');
          }
          return;
        }
        try {
          await Promise.all(
            selected.map((id) =>
              api.patch(`/projects/${project.id}/tasks/${id}`, { sprintId: sprint.id })
            )
          );
          toast(
            `${selected.length} task${selected.length === 1 ? '' : 's'} pulled into ${sprint.name}`
          );
          close();
          onChanged();
        } catch (e: any) {
          if (errorEl) {
            errorEl.textContent = e.message || 'Could not pull tasks';
            errorEl.classList.remove('hidden');
          }
        }
      });
    }
  );
}
